/**
 * Widget Constants
 *
 * Shared constants used across widget components and utilities.
 */

import { WidgetView } from "../types";

/**
 * Animation duration for exit transitions (milliseconds).
 */
export const exitAnimationDuration = 200;

/**
 * Maximum characters for main content extraction.
 * Keeps payload size reasonable for API transmission.
 */
export const maxContentLength = 10_000;

/**
 * Maximum number of image alt texts to extract.
 * Limits payload while capturing key visual context.
 */
export const maxImages = 10;

/**
 * Widget container element ID.
 */
export const widgetContainerId = "pw-root";

/**
 * Where the widget mounts in the partner page. The three `bottom-*` values
 * are desktop anchors for the floating surfaces (fixed-position over the
 * viewport); mobile (< md / 768px) collapses all three to bottom-centre.
 * `inline` mounts in normal document flow at the script tag's location,
 * adapts to parent width, and keeps that placement at every viewport.
 */
export const WidgetPosition = {
  BottomLeft: "bottom-left",
  BottomCenter: "bottom-center",
  BottomRight: "bottom-right",
  Inline: "inline",
} as const;
export type WidgetPosition =
  (typeof WidgetPosition)[keyof typeof WidgetPosition];

export const defaultWidgetPosition: WidgetPosition = WidgetPosition.BottomRight;

// Floating mobile baseline: 4px from the bottom, centred horizontally. Desktop
// slot overrides apply via the `md:` prefix.
//
// Centring uses `inset-x-0 + mx-auto` instead of `left-1/2 + -translate-x-1/2`
// for two reasons:
//   1. Tailwind v4's translate utilities emit the modern `translate` CSS
//      property through a `var(--tw-translate-y)` chain that requires CSS
//      `@property` support to resolve. In contexts where `@property` doesn't
//      register (some browsers, some Shadow-DOM edge cases) the var becomes
//      invalid and the whole `translate` declaration is dropped — leaving the
//      element anchored at `left: 50%` with its left edge at viewport centre.
//   2. The slideUp animation animates `transform: translateY(...)`. Box-model
//      centring composes cleanly with that; transform-based centring would
//      have to share the property with the animation.
//
// `inset-x-0 + mx-auto` requires a definite width, so every floating surface
// carries an explicit pixel width.
//
// `pointer-events-none` because the fixed box now extends 24px above the
// surface to cover the AdvertisementLabel band. That strip is transparent but
// would still swallow clicks meant for the partner content underneath it, so
// the wrapper drops pointer events and each surface takes them back with
// `surfaceClickTargetClass`. Setting it on the label row alone is not enough —
// the click then lands on the wrapper instead. Inline needs none of this: the
// band is in document flow there, over nothing.
const floatingPositionMobileBase =
  "pw:fixed pw:z-50 pw:bottom-1 pw:inset-x-0 pw:mx-auto pw:md:bottom-4 pw:pointer-events-none";

/**
 * Restores pointer events on anything interactive inside a floating wrapper
 * (the surface chrome itself, plus the preview's close button, which sits in
 * the label row rather than the surface). Pairs with the `pointer-events-none`
 * in `floatingPositionMobileBase`.
 */
export const surfaceClickTargetClass = "pw:pointer-events-auto";

/**
 * Resolved class strings per position. The three floating entries share the
 * mobile baseline plus desktop overrides that release one inset edge to
 * anchor to a viewport corner. The `inline` entry uses normal document flow
 * — no `fixed`, no `z-index`, no insets — so the widget sits where the
 * script tag is pasted and scrolls with the surrounding content. Each
 * consuming component picks the entry matching the active config.
 */
export const floatingPositionClassByPosition: Record<WidgetPosition, string> = {
  [WidgetPosition.BottomLeft]: `${floatingPositionMobileBase} pw:md:left-4 pw:md:right-auto pw:md:mx-0`,
  [WidgetPosition.BottomCenter]: floatingPositionMobileBase,
  [WidgetPosition.BottomRight]: `${floatingPositionMobileBase} pw:md:left-auto pw:md:right-4 pw:md:mx-0`,
  // `pw:block` because the position class is concatenated onto each
  // surface's own static classes — Preview / Card already declare their
  // own `pw:flex pw:items-center pw:justify-between` (horizontal layout
  // for image+title and CTA), and an outer `pw:flex-col` would override
  // that and break the chrome.
  //
  // `pw:mx-auto` is a no-op now that every inline surface is `w-full`; it is
  // kept so a future `w-max` surface still centres in the host's slot.
  //
  // Pill in a Preview-sized reserved slot (`min-height` from
  // `widgetSurfaceMinHeight.preview`) leaves visible space below the pill;
  // accepted trade-off so toggling to Pill does not reflow the article.
  [WidgetPosition.Inline]: "pw:block pw:mx-auto",
};

/**
 * Width class string for a surface at a given position. Floating positions
 * keep their Figma-spec width (394px, or 378px for the pill) with a viewport
 * cap so the surfaces don't overflow narrow phone viewports. Inline lets the parent container drive the
 * width: every surface fills the article column, the pill included, at every
 * viewport — a compact pill in a full-width slot looks like a stray button
 * rather than embedded content.
 */
export function widgetSurfaceWidthClass(
  position: WidgetPosition,
  surface: WidgetView,
): string {
  if (position === WidgetPosition.Inline) {
    return "pw:w-full";
  }
  // 378px per Figma. The copy names the destination at every position
  // (APPS-5583) and is wider than that, so a `w-max` pill would run past a
  // phone's viewport on one line; a fixed width folds it to two instead. Same
  // viewport clamp as the other floating surfaces.
  if (surface === "pill") {
    return "pw:w-[23.625rem] pw:max-w-[calc(100vw-0.5rem)]";
  }
  // `max-w-[calc(100vw-0.5rem)]` is intentional and load-bearing — without
  // it the 394px (24.625rem) fixed width overflows the 4px floating margin
  // budget on phones narrower than ~402px. Keep both classes together.
  return "pw:w-[24.625rem] pw:max-w-[calc(100vw-0.5rem)]";
}

/**
 * Enter/exit animation class for a surface at a given position. Floating
 * surfaces slide up from the viewport edge (they rise into an overlay), with
 * no fade: opacity below 1 suppresses `backdrop-filter`, so a fade would drop
 * the frosted blur for the length of the animation. Inline surfaces sit in
 * document flow over the slot the loading skeleton reserved, so a vertical
 * slide fights the static placeholder — they fade in place instead (with a
 * small upward settle), giving a soft skeleton → content hand-off. Inline
 * keeps its fade and therefore its blur flicker: the exit is opacity-only, so
 * removing it would leave the surface vanishing with no transition at all.
 * Disabled under `prefers-reduced-motion` for the inline fade.
 */
export function surfaceAnimationClass(
  position: WidgetPosition,
  isExiting: boolean,
): string {
  if (position === WidgetPosition.Inline) {
    return isExiting
      ? "pw:animate-fadeOut pw:motion-reduce:animate-none"
      : "pw:animate-fadeIn pw:motion-reduce:animate-none";
  }
  return isExiting ? "pw:animate-slideDown" : "pw:animate-slideUp";
}

// Gap below an inline widget. Goes on the in-flow root (InlineStage, skeleton
// wrapper), not a surface inside the stage — the stage pins its height to the
// surface's offsetHeight, which excludes margins.
export const inlineTrailingSpaceClass = "pw:mb-4";

/**
 * Natural rendered height per surface, used by the inline mount path to
 * reserve DOM space for the default state before the async data resolves
 * — without this the partner article visibly jumps when the widget first
 * hydrates. Values mirror the height utilities in each surface component
 * (`pw:min-h-12`, `pw:min-h-[4.5rem]`, etc.); see `mountContainer` in
 * `widget-loader.ts`.
 */
export const widgetSurfaceMinHeight: Record<WidgetView, string> = {
  // 87px — the inline pill is sized by its 55px brand mark plus `py-1` (63px),
  // not by the 48px `min-h-12` floor, + the 24px AdvertisementLabel band.
  pill: "5.4375rem",
  // 96px — 72px surface (`pw:min-h-[4.5rem]`) + the 24px AdvertisementLabel
  // band above it, which is sized to the close button it carries.
  preview: "6rem",
  // ~504px — typical binary card height per Figma master 8079:23380 (432px)
  // + the 24px AdvertisementLabel band above it + the closing source
  // disclosure (two 16px lines plus its 16px `mt-4`). The disclosure only
  // fits on one line from ~500px of card width up, which no inline column
  // this reserve is used for reaches in practice.
  card: "31.5rem",
};

/**
 * Shared visual shell for the Preview surface — layout, sizing, radius, and
 * chrome (border, overlay fill, blur, shadow, font). Position and width are
 * applied separately (`floatingPositionClassByPosition`,
 * `widgetSurfaceWidthClass`). Consumed by both `market-preview.tsx` and the
 * loading `widget-skeleton.tsx` so the two can't drift apart.
 */
export const previewSurfaceShellClasses = [
  "pw:flex pw:items-center pw:justify-between",
  // 72px is the Figma height and what the surface renders at in practice: a
  // 2-line title (2 × 24px) plus the subtitle (16px) plus `py-1` comes to
  // exactly 72px. `min-h` rather than `h` so a wider glyph set or a fallback
  // font grows the pill instead of clipping the subtitle's descenders.
  "pw:min-h-[4.5rem]",
  "pw:pl-1 pw:pr-3 pw:py-1", // 4 / 12 / 4 per Figma
  "pw:rounded-full",
  "pw:border pw:border-widget-border",
  "pw:bg-widget-overlay",
  "pw:backdrop-blur-widget pw:shadow-widget",
  "pw:font-sans",
].join(" ");

/**
 * Shared visual shell for the Pill surface — chrome only (no cursor/animation,
 * which the interactive surface adds). Consumed by `market-pill.tsx` and the
 * loading `widget-skeleton.tsx` so the two can't drift apart.
 */
export const pillSurfaceShellClasses = [
  "pw:flex pw:items-center pw:gap-3", // 12px between the brand mark and the CTA text
  // `min-h` rather than a fixed height: the "through Predictor.io" copy folds
  // to two lines in the floating pill and in a narrow article column, which
  // needs 56px (2 × 24px leading + 8px `py-1`). A wide inline column keeps it
  // on one line and the brand mark sets the height instead.
  "pw:min-h-12 pw:pl-1 pw:pr-4 pw:py-1",
  "pw:rounded-full pw:border pw:border-widget-border",
  "pw:bg-widget-overlay pw:text-type-primary-foreground",
  "pw:backdrop-blur-widget pw:shadow-widget",
  "pw:font-sans",
].join(" ");

/**
 * Shared visual shell for the Card surface — chrome only (position, width, and
 * the enter/exit animation are applied at the call site). Consumed by
 * `market-card.tsx` and the loading `widget-skeleton.tsx` so the two can't
 * drift apart.
 */
export const cardSurfaceShellClasses = [
  "pw:flex pw:flex-col",
  "pw:p-4",
  "pw:rounded-3xl",
  "pw:border pw:border-widget-border",
  "pw:bg-widget-overlay pw:backdrop-blur-widget pw:shadow-widget",
  "pw:font-sans pw:text-type-primary-foreground",
].join(" ");

/**
 * Fallback default surface — used when `data-default-view` is absent, empty,
 * or unrecognised. Both `parseDefaultView` and the widget config default to
 * this; the inline mount path reserves space for the resolved view.
 */
export const defaultWidgetSurface: WidgetView = WidgetView.Preview;

/**
 * Selectors for elements to remove when extracting body content.
 * These typically contain navigation, ads, or non-content elements.
 */
export const noiseSelectors = [
  "nav",
  "footer",
  "aside",
  "header",
  "script",
  "style",
  "noscript",
  "iframe",
  "template",
  ".ad",
  ".advertisement",
  ".ads",
  ".sidebar",
  ".navigation",
  ".menu",
  ".comments",
  '[role="navigation"]',
  '[role="banner"]',
  '[role="contentinfo"]',
  '[role="complementary"]',
  "[data-ad]",
  "[data-advertisement]",
  "[hidden]",
  '[aria-hidden="true"]',
] as const;
